import React from "react";
import { Share2, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Sticky bottom action bar for the mobile Quote tab.
 *
 * - Mobile-only (md:hidden) — fixed to the viewport bottom with safe-area padding
 *   so iPhone home indicators don't overlap the button.
 * - Renders ONE primary action: "Condividi" → opens the existing share dialog
 *   (WhatsApp, Email, PDF, Download) wired by the parent.
 * - Renders the autosave status badge on the left, replacing the legacy
 *   "Salva" button. The badge collapses to icon-only on tight viewports.
 * - Backdrop-blur and 95% white background mirror iOS native nav bars.
 */
export function QuoteMobileActionBar({
  onShare,
  busy = false,
  disabled = false,
  autosaveStatus = "idle",
  dirty = false,
}) {
  let badgeIcon = null;
  let badgeText = "Salvato";
  let badgeTone = "text-emerald-700 bg-emerald-50 border-emerald-200";
  let badgeDot = "bg-emerald-500";

  if (autosaveStatus === "saving") {
    badgeIcon = <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.6} />;
    badgeText = "Salvataggio…";
    badgeTone = "text-amber-700 bg-amber-50 border-amber-200";
    badgeDot = "bg-amber-500";
  } else if (autosaveStatus === "error") {
    badgeIcon = <AlertCircle className="w-3 h-3" strokeWidth={1.7} />;
    badgeText = "Errore";
    badgeTone = "text-red-700 bg-red-50 border-red-200";
    badgeDot = "bg-red-500";
  } else if (dirty) {
    badgeIcon = <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.6} />;
    badgeText = "Salvataggio…";
    badgeTone = "text-amber-700 bg-amber-50 border-amber-200";
    badgeDot = "bg-amber-500";
  } else {
    badgeIcon = (
      <span
        aria-hidden
        className={`w-1.5 h-1.5 rounded-full ${badgeDot}`}
      />
    );
  }

  return (
    <div
      className="md:hidden fixed inset-x-0 bottom-0 z-30 border-t border-neutral-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80"
      style={{
        paddingBottom: "max(env(safe-area-inset-bottom), 0.5rem)",
      }}
      data-testid="quote-mobile-action-bar"
    >
      <div className="flex items-center gap-3 px-3 pt-3 pb-1">
        <div
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium ${badgeTone}`}
          data-testid="quote-mobile-autosave-badge"
          data-status={autosaveStatus}
        >
          {badgeIcon}
          <span>{badgeText}</span>
        </div>
        <button
          type="button"
          onClick={onShare}
          disabled={busy || disabled}
          className="flex-1 h-12 rounded-xl bg-neutral-900 text-white font-semibold text-[15px] inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50 disabled:active:scale-100"
          data-testid="quote-mobile-share-button"
        >
          {busy ? (
            <>
              <Loader2 className="w-[18px] h-[18px] animate-spin" strokeWidth={1.7} />
              Preparazione…
            </>
          ) : (
            <>
              <Share2 className="w-[18px] h-[18px]" strokeWidth={1.7} />
              Condividi
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default QuoteMobileActionBar;
